/**
 * FristenReminderSettings – Automatische Fristen-Erinnerungen per E-Mail & Push
 * Mit konfigurierbaren Erinnerungs-Intervallen (14, 7, 3, 1 Tage)
 */
import { useState, useEffect, useCallback } from 'react';
import { Bell, Mail, Clock, Play, RefreshCw, CheckCircle, XCircle, Calendar, Plus, X, Smartphone, BellRing } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// Verfügbare Intervalle für Checkboxen
const AVAILABLE_INTERVALS = [14, 7, 3, 1];

/**
 * Konvertiert base64-urlsafe VAPID Key zu Uint8Array für PushManager
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function FristenReminderSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [preview, setPreview] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  
  // Push-Benachrichtigungen State
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSubscription, setPushSubscription] = useState(null);
  const [pushSettings, setPushSettings] = useState(null);
  const [pushLoading, setPushLoading] = useState(false);
  const [testingPush, setTestingPush] = useState(false);

  useEffect(() => {
    loadSettings();
    loadLogs();
    checkPushSupport();
    loadPushSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Prüft ob Push-Benachrichtigungen im Browser unterstützt werden
   */
  const checkPushSupport = useCallback(async () => {
    const supported = 'Notification' in window;
    setPushSupported(supported);
    if (supported) {
      setPushPermission(Notification.permission);
    }
  }, []);

  /**
   * Lädt die Push-Einstellungen vom Server
   */
  const loadPushSettings = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/push/settings`);
      const data = await res.json();
      setPushSettings(data.settings);
    } catch (e) {
      console.error('Fehler beim Laden der Push-Einstellungen:', e);
    }
  };

  /**
   * Aktiviert Push-Benachrichtigungen
   */
  const enablePushNotifications = async () => {
    setPushLoading(true);
    try {
      // 1. Berechtigung anfordern
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      
      if (permission !== 'granted') {
        toast.error('Push-Benachrichtigungen wurden abgelehnt');
        return;
      }
      
      // 2. VAPID Public Key vom Server holen
      const vapidRes = await fetch(`${BACKEND_URL}/api/push/vapid-public-key`);
      const vapidData = await vapidRes.json();
      
      if (!vapidData.success || !vapidData.publicKey) {
        toast.error('Push-Server nicht konfiguriert');
        return;
      }
      
      // 3. Push-Subscription erstellen (Service Worker deaktiviert)
      toast.error('Push-Benachrichtigungen erfordern einen aktiven Service Worker');
      return;
      
      // 5. Subscription zum Server senden
      const userId = localStorage.getItem('userId') || 'anonymous';
      const subRes = await fetch(`${BACKEND_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
            auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
          },
          user_id: userId
        })
      });
      
      if (subRes.ok) {
        setPushSubscription(subscription);
        toast.success('Push-Benachrichtigungen aktiviert! 🔔');
      } else {
        throw new Error('Server-Fehler');
      }
    } catch (e) {
      console.error('Fehler beim Aktivieren der Push-Benachrichtigungen:', e);
      toast.error('Push-Benachrichtigungen konnten nicht aktiviert werden');
    } finally {
      setPushLoading(false);
    }
  };

  /**
   * Deaktiviert Push-Benachrichtigungen
   */
  const disablePushNotifications = async () => {
    setPushLoading(true);
    try {
      if (pushSubscription) {
        // Server-seitig entfernen
        await fetch(`${BACKEND_URL}/api/push/unsubscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint: pushSubscription.endpoint })
        });
        
        // Browser-seitig unsubscribe
        await pushSubscription.unsubscribe();
        setPushSubscription(null);
        toast.success('Push-Benachrichtigungen deaktiviert');
      }
    } catch (e) {
      console.error('Fehler beim Deaktivieren:', e);
      toast.error('Fehler beim Deaktivieren');
    } finally {
      setPushLoading(false);
    }
  };

  /**
   * Sendet eine Test-Push-Benachrichtigung
   */
  const sendTestPush = async () => {
    setTestingPush(true);
    try {
      const userId = localStorage.getItem('userId') || 'anonymous';
      const res = await fetch(`${BACKEND_URL}/api/push/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId })
      });
      const data = await res.json();
      
      if (data.success && data.sent > 0) {
        toast.success('Test-Benachrichtigung gesendet!');
      } else {
        toast.info('Keine Geräte registriert');
      }
    } catch (e) {
      toast.error('Fehler beim Senden der Test-Benachrichtigung');
    } finally {
      setTestingPush(false);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/fristen/reminder/settings`);
      const data = await res.json();
      setSettings(data.settings);
    } catch (e) {
      console.error('Fehler beim Laden der Einstellungen:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    setLoadingLogs(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/fristen/reminder/log`);
      const data = await res.json();
      setLogs(data.logs || []);
    } catch (e) {
      console.error('Fehler beim Laden der Logs:', e);
    } finally {
      setLoadingLogs(false);
    }
  };

  const loadPreview = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/fristen/reminder/preview`);
      const data = await res.json();
      setPreview(data);
    } catch (e) {
      toast.error('Fehler beim Laden der Vorschau');
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/fristen/reminder/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      if (res.ok) {
        toast.success('Einstellungen gespeichert');
        loadSettings(); // Neu laden um sortierte Werte zu erhalten
      } else {
        throw new Error();
      }
    } catch (e) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const triggerReminder = async () => {
    setTriggering(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/fristen/reminder/trigger`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success(`${data.emails_sent} E-Mail(s) gesendet!`);
      } else if (data.errors?.length > 0) {
        toast.error(data.errors[0]);
      } else {
        toast.info('Keine Fristen zum Erinnern gefunden');
      }
      
      loadLogs();
    } catch (e) {
      toast.error('Fehler beim Auslösen der Erinnerungen');
    } finally {
      setTriggering(false);
    }
  };

  const toggleInterval = (days) => {
    const current = settings?.tage_vorher || [];
    let updated;
    if (current.includes(days)) {
      updated = current.filter(d => d !== days);
    } else {
      updated = [...current, days];
    }
    // Mindestens ein Intervall muss aktiv sein
    if (updated.length === 0) {
      toast.error('Mindestens ein Intervall muss aktiv sein');
      return;
    }
    setSettings({ ...settings, tage_vorher: updated.sort((a, b) => b - a) });
  };

  const getIntervalColor = (days) => {
    if (days <= 1) return 'bg-red-100 border-red-300 text-red-700';
    if (days <= 3) return 'bg-orange-100 border-orange-300 text-orange-700';
    if (days <= 7) return 'bg-yellow-100 border-yellow-300 text-yellow-700';
    return 'bg-blue-100 border-blue-300 text-blue-700';
  };

  const getIntervalBadgeColor = (days) => {
    if (days <= 1) return 'bg-red-500';
    if (days <= 3) return 'bg-orange-500';
    if (days <= 7) return 'bg-yellow-500';
    return 'bg-blue-500';
  };

  if (loading) {
    return <div className="p-4 text-slate-500">Laden...</div>;
  }

  const activeIntervals = settings?.tage_vorher || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
            <Bell className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800">Fristen-Erinnerungen</h3>
            <p className="text-sm text-slate-500">Automatische E-Mail-Benachrichtigungen bei fälligen Fristen</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={settings?.aktiv || false}
            onCheckedChange={(checked) => setSettings({ ...settings, aktiv: checked })}
            id="reminder-aktiv"
          />
          <Label htmlFor="reminder-aktiv" className="text-sm">
            {settings?.aktiv ? 'Aktiv' : 'Inaktiv'}
          </Label>
        </div>
      </div>

      {/* Info-Box mit Zeitplan */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-800">
              <strong>Automatischer Ablauf:</strong> Jeden Tag um <strong>{settings?.uhrzeit || '08:00'} Uhr</strong> werden 
              E-Mails an die zuständigen Betreuer gesendet.
            </p>
            <p className="text-xs text-blue-600 mt-1">
              Die E-Mail enthält eine strukturierte Übersicht aller betroffenen Klienten und Fristen.
            </p>
          </div>
        </div>
      </div>

      {/* Push-Benachrichtigungen Sektion */}
      <div className="bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h4 className="font-semibold text-slate-800">Push-Benachrichtigungen</h4>
              <p className="text-sm text-slate-500">
                Erhalten Sie Benachrichtigungen direkt auf Ihrem Gerät
              </p>
            </div>
          </div>
          
          {pushSupported ? (
            <div className="flex items-center gap-2">
              {pushSubscription ? (
                <span className="flex items-center gap-1.5 text-sm text-green-600 bg-green-100 px-3 py-1 rounded-full">
                  <CheckCircle className="w-4 h-4" />
                  Aktiviert
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-sm text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                  <XCircle className="w-4 h-4" />
                  Deaktiviert
                </span>
              )}
            </div>
          ) : (
            <span className="text-sm text-red-500">Nicht unterstützt</span>
          )}
        </div>
        
        {pushSupported && (
          <div className="flex flex-wrap gap-2">
            {!pushSubscription ? (
              <Button
                onClick={enablePushNotifications}
                disabled={pushLoading || pushPermission === 'denied'}
                size="sm"
                className="gap-2 bg-violet-600 hover:bg-violet-700"
              >
                {pushLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <BellRing className="w-4 h-4" />
                )}
                Push aktivieren
              </Button>
            ) : (
              <>
                <Button
                  onClick={disablePushNotifications}
                  disabled={pushLoading}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  {pushLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  Push deaktivieren
                </Button>
                <Button
                  onClick={sendTestPush}
                  disabled={testingPush}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  {testingPush ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Bell className="w-4 h-4" />
                  )}
                  Test senden
                </Button>
              </>
            )}
          </div>
        )}
        
        {pushPermission === 'denied' && (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <strong>Hinweis:</strong> Push-Benachrichtigungen wurden im Browser blockiert. 
            Bitte erlauben Sie Benachrichtigungen in den Browser-Einstellungen.
          </div>
        )}
        
        {pushSettings?.fristen_push_aktiv && (
          <div className="text-xs text-violet-600">
            Push-Benachrichtigungen werden bei Fristen in {pushSettings.fristen_tage?.join(' und ')} Tag(en) gesendet.
          </div>
        )}
      </div>

      {/* Erinnerungs-Intervalle */}
      <div>
        <Label className="text-sm font-medium text-slate-700 mb-3 block">
          Erinnerungs-Intervalle (Tage vor Fristablauf)
        </Label>
        <div className="flex flex-wrap gap-2">
          {AVAILABLE_INTERVALS.map(days => {
            const isActive = activeIntervals.includes(days);
            return (
              <button
                key={days}
                onClick={() => toggleInterval(days)}
                className={`px-4 py-2 rounded-lg border-2 font-medium text-sm transition-all ${
                  isActive 
                    ? getIntervalColor(days) + ' shadow-sm' 
                    : 'bg-slate-50 border-slate-200 text-slate-400 hover:bg-slate-100'
                }`}
              >
                {isActive && <CheckCircle className="w-4 h-4 inline mr-1.5" />}
                {days} {days === 1 ? 'Tag' : 'Tage'}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Klicken Sie auf die Intervalle um sie zu aktivieren/deaktivieren. 
          Bei Fristen die in der gewählten Anzahl Tage fällig sind, wird eine E-Mail gesendet.
        </p>
      </div>

      {/* Aktionen */}
      <div className="flex flex-wrap gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={loadPreview}
          className="gap-2"
        >
          <Calendar className="w-4 h-4" />
          Vorschau anzeigen
        </Button>
        <Button
          variant="default"
          size="sm"
          onClick={triggerReminder}
          disabled={triggering}
          className="gap-2"
        >
          {triggering ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          Jetzt auslösen
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={saveSettings}
          disabled={saving}
          className="gap-2"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
          Speichern
        </Button>
      </div>

      {/* Vorschau */}
      {preview && (
        <div className="bg-slate-50 rounded-lg p-4 space-y-3">
          <h4 className="font-medium text-slate-700 flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Vorschau: Fristen die Erinnerungen auslösen würden
          </h4>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {(preview.konfigurierte_intervalle || activeIntervals).map(days => {
              const data = preview.fristen_by_days?.[days] || { anzahl: 0, fristen: [] };
              return (
                <div 
                  key={days} 
                  className={`rounded-lg p-3 border ${getIntervalColor(days)}`}
                >
                  <div className="text-2xl font-bold">{data.anzahl}</div>
                  <div className="text-sm">
                    Fristen in {days} {days === 1 ? 'Tag' : 'Tagen'}
                  </div>
                  {data.fristen?.length > 0 && (
                    <ul className="mt-2 text-xs opacity-80 space-y-0.5">
                      {data.fristen.slice(0, 2).map((f, i) => (
                        <li key={i} className="truncate">• {f.klient_name}</li>
                      ))}
                      {data.fristen.length > 2 && (
                        <li className="opacity-60">... +{data.fristen.length - 2} weitere</li>
                      )}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
          
          {preview.gesamt === 0 && (
            <p className="text-sm text-slate-500 italic">
              Aktuell gibt es keine Fristen, die genau an den konfigurierten Tagen fällig sind.
            </p>
          )}
        </div>
      )}

      {/* Letzte Ausführungen */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-slate-700">Letzte Ausführungen</h4>
          <Button variant="ghost" size="sm" onClick={loadLogs} disabled={loadingLogs}>
            <RefreshCw className={`w-4 h-4 ${loadingLogs ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        
        {logs.length === 0 ? (
          <p className="text-sm text-slate-400 italic">Noch keine Erinnerungen gesendet.</p>
        ) : (
          <div className="space-y-2">
            {logs.slice(0, 5).map((log, i) => (
              <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-lg p-3 text-sm">
                {log.success ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-500" />
                )}
                <div className="flex-1">
                  <span className="text-slate-600">
                    {new Date(log.timestamp).toLocaleString('de-DE')}
                  </span>
                  <span className="mx-2 text-slate-300">|</span>
                  <span className="text-slate-700">
                    {log.emails_sent} E-Mail(s) gesendet
                  </span>
                  {log.intervalle_konfiguriert && (
                    <>
                      <span className="mx-2 text-slate-300">|</span>
                      <span className="text-slate-500 text-xs">
                        Intervalle: {log.intervalle_konfiguriert.join(', ')}T
                      </span>
                    </>
                  )}
                </div>
                {log.errors?.length > 0 && (
                  <span className="text-xs text-red-500 truncate max-w-[200px]">{log.errors[0]}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Hinweis */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
        <strong>Voraussetzung:</strong> Die E-Mail-Einstellungen (SMTP) müssen korrekt konfiguriert sein, 
        damit Erinnerungen versendet werden können.
      </div>
    </div>
  );
}
